import React, { useState, useEffect } from "react";
import { FaTimes, FaCheckCircle, FaTimesCircle, FaUser } from "react-icons/fa";
import { collaborationApi } from "../../services/api/collaborationApi";
import { toast } from "react-toastify";
import UserAvatar from "./UserAvatar";
import "./ManageApplicationsModal.css";

const ManageApplicationsModal = ({ isOpen, onClose, postId, onUpdate }) => {
  const [applicationsData, setApplicationsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [reviewingApp, setReviewingApp] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && postId) {
      loadApplications();
    }
  }, [isOpen, postId]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const result = await collaborationApi.getPostApplications(postId);
      if (result.data.success) {
        setApplicationsData(result.data.data);
      }
    } catch (error) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (applicationId, decision) => {
    setSubmitting(true);
    try {
      const result = await collaborationApi.reviewApplication(
        postId,
        applicationId,
        decision,
        reviewNote.trim()
      );

      if (result.data.success) {
        // Optimistic update - update local state
        const updatedApps = applicationsData.applications.map((app) =>
          app._id === applicationId
            ? {
                ...app,
                status: decision,
                reviewedAt: new Date(),
                reviewNote: reviewNote.trim(),
              }
            : app
        );

        setApplicationsData({
          ...applicationsData,
          applications: updatedApps,
          pendingCount:
            decision === "accepted" || decision === "rejected"
              ? applicationsData.pendingCount - 1
              : applicationsData.pendingCount,
          acceptedCount:
            decision === "accepted"
              ? applicationsData.acceptedCount + 1
              : applicationsData.acceptedCount,
          rejectedCount:
            decision === "rejected"
              ? applicationsData.rejectedCount + 1
              : applicationsData.rejectedCount,
        });

        toast.success(
          `Application ${decision === "accepted" ? "accepted" : "rejected"}!`
        );

        if (result.data.addedToRoom) {
          toast.success("Applicant added to research room!");
        }

        // Clear review state
        setReviewingApp(null);
        setReviewNote("");

        // Update parent component
        if (onUpdate) {
          onUpdate(postId, {});
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to review application"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseReview = () => {
    if (!submitting) {
      setReviewingApp(null);
      setReviewNote("");
    }
  };

  if (!isOpen) return null;

  const filteredApplications =
    applicationsData?.applications.filter((app) => {
      if (filter === "all") return true;
      return app.status === filter;
    }) || [];

  return (
    <div className="manage-modal-overlay" onClick={onClose}>
      <div
        className="manage-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="manage-modal-header">
          <h2>Manage Applications</h2>
          <button className="manage-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Statistics */}
        {applicationsData && (
          <div className="applications-stats">
            <div className="stat-item">
              <span className="stat-label">Total</span>
              <span className="stat-value">
                {applicationsData.totalApplications}
              </span>
            </div>
            <div className="stat-item pending">
              <span className="stat-label">Pending</span>
              <span className="stat-value">
                {applicationsData.pendingCount}
              </span>
            </div>
            <div className="stat-item accepted">
              <span className="stat-label">Accepted</span>
              <span className="stat-value">
                {applicationsData.acceptedCount}
              </span>
            </div>
            <div className="stat-item rejected">
              <span className="stat-label">Rejected</span>
              <span className="stat-value">
                {applicationsData.rejectedCount}
              </span>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="applications-filters">
          <button
            className={`filter-tab ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            className={`filter-tab ${filter === "pending" ? "active" : ""}`}
            onClick={() => setFilter("pending")}
          >
            Pending
          </button>
          <button
            className={`filter-tab ${filter === "accepted" ? "active" : ""}`}
            onClick={() => setFilter("accepted")}
          >
            Accepted
          </button>
          <button
            className={`filter-tab ${filter === "rejected" ? "active" : ""}`}
            onClick={() => setFilter("rejected")}
          >
            Rejected
          </button>
        </div>

        {/* Applications List */}
        <div className="applications-list">
          {loading ? (
            <div className="loading-state">Loading applications...</div>
          ) : filteredApplications.length === 0 ? (
            <div className="empty-state">
              No {filter !== "all" ? filter : ""} applications found
            </div>
          ) : (
            filteredApplications.map((app) => (
              <div key={app._id} className="application-card">
                {/* Applicant Info */}
                <div className="applicant-header">
                  <div className="applicant-info">
                    <UserAvatar
                      user={app.user}
                      size="medium"
                      className="applicant-avatar"
                    />
                    <div className="applicant-details">
                      <h4 className="applicant-name">{app.user.name}</h4>
                      {app.user.professionalRole && (
                        <p className="applicant-role">
                          {app.user.professionalRole}
                        </p>
                      )}
                      {app.user.field && (
                        <p className="applicant-field">{app.user.field}</p>
                      )}
                    </div>
                  </div>
                  <span
                    className={`application-status-badge status-${app.status}`}
                  >
                    {app.status}
                  </span>
                </div>

                {/* Position Applied For */}
                {app.positionIndex !== undefined && (
                  <div className="applied-position">
                    <strong>Position:</strong> Position {app.positionIndex + 1}
                  </div>
                )}

                {/* Motivation */}
                <div className="motivation-section">
                  <h5>Motivation</h5>
                  <p className="motivation-text">{app.motivation}</p>
                </div>

                {/* Applied Date */}
                <div className="application-meta">
                  <span className="meta-date">
                    Applied on{" "}
                    {new Date(app.appliedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                </div>

                {/* Review Note (if reviewed) */}
                {app.reviewNote && (
                  <div className="review-note-display">
                    <strong>Review Note:</strong> {app.reviewNote}
                  </div>
                )}

                {/* Actions */}
                {app.status === "pending" && (
                  <div className="application-actions">
                    {reviewingApp === app._id ? (
                      <div className="review-form">
                        <textarea
                          value={reviewNote}
                          onChange={(e) => setReviewNote(e.target.value)}
                          placeholder="Add a note for the applicant (optional, max 500 chars)"
                          maxLength={500}
                          rows={3}
                          disabled={submitting}
                        />
                        <div className="review-actions">
                          <button
                            className="review-cancel-btn"
                            onClick={handleCloseReview}
                            disabled={submitting}
                          >
                            Cancel
                          </button>
                          <button
                            className="review-reject-btn"
                            onClick={() => handleReview(app._id, "rejected")}
                            disabled={submitting}
                          >
                            <FaTimesCircle /> Reject
                          </button>
                          <button
                            className="review-accept-btn"
                            onClick={() => handleReview(app._id, "accepted")}
                            disabled={submitting}
                          >
                            <FaCheckCircle /> Accept
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        className="review-btn"
                        onClick={() => setReviewingApp(app._id)}
                      >
                        Review Application
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageApplicationsModal;
